import React, { useEffect, useState } from "react";
import { Box, Typography, Button, TextField, Card } from "@mui/material";
import { json, Link } from "react-router-dom";
import {
  resendEmailOTP,
  verifyEmailOTP,
  verifyPhoneOTP,
  resendPhoneOTP,
} from "service/axiosConfig";
import CodeInput from "components/CodeInput";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { PROD_URL } from "assets/constants";
import { useDispatch } from "react-redux";
import { login } from "../redux/reducers/authSlice";
import { setEncodedUserProfile, getEncodedUserProfile } from "../utils/authHelpers";
import capitalize from "capitalize";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export default function VerificationScreen() {
  const [userProfile, setUserProfile] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const verifyToken = async (token) => {
    try {
      const response = await axios.get(`${PROD_URL}auth/magic-login?token=${token}`);
      const res = response.data;
      console.log(res);

      // If verification is complete
      if (res?.data?.verificationComplete) {
        await localStorage.setItem("accessToken", res?.data.token);
        await localStorage.setItem("refreshToken", res?.data.refreshToken);
        await localStorage.setItem("userId", res?.data.userId);
        await localStorage.setItem("isAuthenticated", true);
        await localStorage.setItem("verifiedUserPlan", res?.data.verifiedUserPlan);
        const verifiedUserPlan = parseInt(localStorage.getItem("verifiedUserPlan") || "0", 10);
        if (verifiedUserPlan === 0) {
          await localStorage.setItem("planStatus", "NoPlan");
        } else if (res?.data.isSubscriptionExpired) {
          await localStorage.setItem("planStatus", "Expired");
        } else {
          await localStorage.setItem("planStatus", "Active");
        }
        setEncodedUserProfile(res?.data);

        dispatch(
          login({
            accessToken: res.data.token,
            userId: res?.data?.userId,
            refreshToken: res?.data?.refreshToken,
          })
        );

        toast.success("Logged in successfully!");
        const preSelectedPlan = localStorage.getItem("preSelectedPlan");
        // Retrieve the value from localStorage
        const fromSignUp = localStorage.getItem("_s44");

        if (preSelectedPlan) {
          navigate("/plans", { state: { fromWordPress: true } });
        } else if (verifiedUserPlan === 0) {
          toast.info("Please select a plan to start using Heartline.");
          navigate("/plans");
        } else if (fromSignUp !== null && fromSignUp === "true") {
          navigate("/settings");
        } else {
          navigate("/callhistory");
        }
      }
      // If verification is NOT complete
      else {
        setEncodedUserProfile(res?.data);
        toast.warning("Please complete verification.");
        navigate("/verification");
      }
    } catch (err) {
      console.log(err);
      toast.error(err?.response?.data || "Login failed");
      navigate("/authentication/sign-in");
    }
  };

  useEffect(() => {
    const profile = getEncodedUserProfile();
    setUserProfile(profile);
  }, []);

  const handleEmailCodeVerify = async (code) => {
    try {
      await verifyEmailOTP(userProfile.email, code);

      // Only if API succeeds, update state
      const updatedProfile = {
        ...userProfile,
        emailOtpVerification: true,
      };
      setUserProfile(updatedProfile);
      if (updatedProfile.emailOtpVerification && updatedProfile.phoneOtpVerification) {
        await verifyToken(updatedProfile.magicToken);
      }
    } catch (err) {
      // We don’t update state if API fails
    }
  };

  const handlePhoneCodeVerify = async (code) => {
    try {
      await verifyPhoneOTP(userProfile.email, userProfile.phoneNumber, code);

      const updatedProfile = {
        ...userProfile,
        phoneOtpVerification: true,
      };
      setUserProfile(updatedProfile);
      if (updatedProfile.emailOtpVerification && updatedProfile.phoneOtpVerification) {
        await verifyToken(updatedProfile.magicToken);
      }
    } catch (err) {
      // Don’t update state if failed
    }
  };

  if (!userProfile) return null;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(to bottom right, #fff7f0, #ffffff, #f0fff7)",
        fontFamily:
          'var(--default-font-family, ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji")',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          borderBottom: "1px solid #e2e8f0",
          bgcolor: "rgba(255,255,255,0.8)",
          backdropFilter: "blur(4px)",
        }}
      >
        <Box
          sx={{
            maxWidth: "1200px",
            mx: "auto",
            px: 2,
            py: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            //onClick={() => navigate("/")}
            onClick={() => (window.location.href = "https://myheartline.com")}
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: "none",
              color: "#475569",
              fontFamily: "inherit",
              fontSize: "16px", // smaller
              fontWeight: 400, // not bold
              "&:hover": { color: "#0f172a" },
            }}
          >
            Back to Home
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                bgcolor: "#7e2a0c",
                borderRadius: "12px",
              }}
            >
              {/* Client’s Heart SVG */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="white"
                stroke="white" // stroke inherits white
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  width: "20px", // matches w-5
                  height: "20px", // matches h-5
                  display: "block",
                  verticalAlign: "middle",
                }}
              >
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
              </svg>
            </Box>
            <Typography
              fontWeight="bold"
              sx={{
                fontFamily: "inherit",
                fontSize: "20px",
                color: "#0f172a",
              }}
            >
              Heartline
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <div
        style={{
          textAlign: "center",
          maxWidth: "720px !important",
          margin: "2rem auto",
          fontFamily: "inherit",
          color: "#4b0000",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <h1 style={{ fontSize: "2rem", fontWeight: "600", marginBottom: "0.5rem" }}>
            Verification For:{" "}
            <span style={{ fontSize: "2rem", fontWeight: "400", marginLeft: "0.5rem" }}>
              {capitalize.words(userProfile.name)}
            </span>
          </h1>
          <p style={{ fontSize: "1rem", color: "#4b0000", marginBottom: "3rem" }}>
            Please verify your email and phone number to continue.
          </p>
        </div>

        {/* Email Section */}
        <Box mb={4} textAlign="left">
          <Typography>Email sent to: {userProfile.email}</Typography>

          <Box display="flex" alignItems="center" gap={2} mt={2}>
            <Button
              sx={{
                backgroundColor: userProfile.emailOtpVerification ? "#561919" : "#FFD700",
                color: userProfile.emailOtpVerification ? "#fff" : "#000",
                "&:hover": {
                  backgroundColor: userProfile.emailOtpVerification ? "#6A1B1B" : "#FFC400",
                  color: userProfile.emailOtpVerification ? "#fff" : "#000",
                },
              }}
            >
              {userProfile.emailOtpVerification ? "Verified" : "Pending"}
            </Button>

            <Button
              sx={{
                backgroundColor: "#000",
                color: "#fff",
                "&:hover": { backgroundColor: "#333", color: "#fff" },
              }}
              onClick={async () => {
                await resendEmailOTP(userProfile.email);
              }}
            >
              Re-Send
            </Button>
          </Box>

          {!userProfile.emailOtpVerification && (
            <>
              <Typography mt={2} fontWeight="600">
                Enter Code:
              </Typography>
              <CodeInput onComplete={(code) => handleEmailCodeVerify(code)} />
            </>
          )}
        </Box>

        <hr />

        {/* Phone Section */}
        <Box mt={4} mb={4} textAlign="left">
          <Typography>SMS sent to: {userProfile.phoneNumber}</Typography>

          <Box display="flex" alignItems="center" gap={2} mt={2}>
            <Button
              sx={{
                backgroundColor: userProfile.phoneOtpVerification ? "#561919" : "#FFD700",
                color: userProfile.phoneOtpVerification ? "#fff" : "#000",
                "&:hover": {
                  backgroundColor: userProfile.phoneOtpVerification ? "#6A1B1B" : "#FFC400",
                  color: userProfile.emailOtpVerification ? "#fff" : "#000",
                },
              }}
            >
              {userProfile.phoneOtpVerification ? "Verified" : "Pending"}
            </Button>

            <Button
              sx={{
                backgroundColor: "#000",
                color: "#fff",
                "&:hover": { backgroundColor: "#333", color: "#fff" },
              }}
              onClick={async () => {
                await resendPhoneOTP(userProfile.phoneNumber);
              }}
            >
              Re-Send
            </Button>
          </Box>

          {!userProfile.phoneOtpVerification && (
            <>
              <Typography mt={2} fontWeight="600">
                Enter Code:
              </Typography>
              <CodeInput onComplete={(code) => handlePhoneCodeVerify(code)} />
            </>
          )}
        </Box>

        <Typography fontSize={12} color="gray" textAlign="center">
          Enter codes to demonstrate control of accounts and devices. Re-send codes if missing or
          expired.
        </Typography>

        <Typography fontSize={12} color="gray" textAlign="center" fontWeight="600">
          Verification Codes will expire in 10 minutes.
        </Typography>
      </div>
    </Box>
  );
}
